import React, { useState, useRef, useEffect } from 'react';

/**
 * A simple color picker component
 * 
 * @param {Object} props
 * @param {string} props.value - Current color value in hex format (#RRGGBB)
 * @param {Function} props.onChange - Callback when color changes
 * @param {string} props.label - Label for the color picker
 * @param {string} props.id - HTML id attribute for the input
 * @param {string} props.name - HTML name attribute for the input
 */
const ColorPicker = ({ 
  value = '#000000', 
  onChange,
  label = '',
  id,
  name,
  className = ''
}) => {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef(null);
  const triggerRef = useRef(null);

  // Handle clicks outside to close the color picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        open && 
        popoverRef.current && 
        !popoverRef.current.contains(event.target) &&
        triggerRef.current && 
        !triggerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  // Predefined color palette
  const colorPalette = [
    // Column 1: Blues
    '#1E40AF', '#1D4ED8', '#3B82F6', '#93C5FD',
    // Column 2: Reds 
    '#B91C1C', '#EF4444', '#FCA5A5', '#FEE2E2',
    // Column 3: Greens
    '#065F46', '#10B981', '#6EE7B7', '#D1FAE5',
    // Column 4: Yellows
    '#92400E', '#F59E0B', '#FCD34D', '#FEF3C7',
    // Column 5: Purples
    '#6D28D9', '#8B5CF6', '#C4B5FD', '#EDE9FE',
    // Column 6: Grays
    '#111827', '#4B5563', '#9CA3AF', '#F3F4F6',
    // Last row: Basic
    '#000000', '#FFFFFF'
  ];

  // Function to organize colors in a vertical order for the grid
  const getReorganizedColors = () => {
    // Group into columns (excluding black and white)
    const columns = [
      ['#1E40AF', '#1D4ED8', '#3B82F6', '#93C5FD'], // Blues
      ['#B91C1C', '#EF4444', '#FCA5A5', '#FEE2E2'], // Reds
      ['#065F46', '#10B981', '#6EE7B7', '#D1FAE5'], // Greens
      ['#92400E', '#F59E0B', '#FCD34D', '#FEF3C7'], // Yellows
      ['#6D28D9', '#8B5CF6', '#C4B5FD', '#EDE9FE'], // Purples
      ['#111827', '#4B5563', '#9CA3AF', '#F3F4F6'], // Grays
    ];
    
    // Reorganize for vertical display (each column contains the same color family)
    const colorGrid = [];
    
    // Add colors in a column-first order
    for (let row = 0; row < 4; row++) {
      for (let col = 0; col < 6; col++) {
        colorGrid.push(columns[col][row]);
      }
    }
    
    // Add black and white at the end
    colorGrid.push('#000000', '#FFFFFF');
    
    return colorGrid;
  };

  // Calculate contrasting text color for better visibility
  const getContrastColor = (hexColor) => {
    // Remove the # if present
    const color = hexColor.charAt(0) === '#' ? hexColor.substring(1) : hexColor;
    
    // Convert to RGB
    const r = parseInt(color.substr(0, 2), 16);
    const g = parseInt(color.substr(2, 2), 16);
    const b = parseInt(color.substr(4, 2), 16);
    
    // Calculate luminance using the relative luminance formula
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      
      <div className="flex items-center">
        <div 
          ref={triggerRef}
          className="w-10 h-10 border border-gray-300 rounded-l cursor-pointer flex-shrink-0" 
          style={{ backgroundColor: value, color: getContrastColor(value) }}
          onClick={() => setOpen(!open)}
        />
        
        <input
          type="text"
          id={id}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 min-w-0 block w-full px-3 py-2 border border-l-0 border-gray-300 rounded-r focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
      
      {open && (
        <div 
          ref={popoverRef}
          className="absolute left-0 mt-1 p-2 bg-white border border-gray-200 rounded-md shadow-lg z-50 w-80"
          style={{ position: 'absolute', top: '100%' }}
        >
          <div className="grid grid-cols-6 gap-1">
            {getReorganizedColors().map((color) => (
              <button
                key={color}
                type="button"
                className="w-full aspect-square rounded border border-gray-300 cursor-pointer hover:scale-110 transition-transform"
                style={{ backgroundColor: color, height: '24px' }}
                onClick={() => {
                  onChange(color);
                  setOpen(false);
                }}
                aria-label={`Select color ${color}`}
              />
            ))}
          </div>
          
          <div className="mt-2">
            <input
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="w-full h-8 cursor-pointer border border-gray-300 rounded"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker; 